import React, { useEffect, useMemo } from "react";
import { Stack } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createFeeAllocation } from "../redux/actions/feeAllocationActions";
import { getStudents } from "../redux/actions/studentActions";
import { getVoteHeads } from "../redux/actions/voteHeadActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { feeAllocationActions } from "../redux/slices/feeAllocation";
import { cleanFormData } from "../helpers/formHelpers";

import { StudentFeeAllocationRequestDto } from "../shared/dtos/studentFeeAllocation/StudentFeeAllocationRequestDto";

const feeAllocationSchema = Yup.object().shape({
  studentId: Yup.string().required("Student selection is required"),
  voteHeadId: Yup.string().required("Vote Head is required"),
  academicYearId: Yup.string().required("Academic Year is required"),
  narrative: Yup.string()
    .max(500, "Narrative cannot exceed 500 characters")
    .nullable(),
});

interface CreateFeeAllocationFormProps {
  loading?: boolean;
  error?: string | null;
}

const CreateFeeAllocationForm: React.FC<CreateFeeAllocationFormProps> = ({
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  // Redux Selectors
  const { items: students } = useAppSelector((state) => state.student);
  const { items: voteHeads } = useAppSelector((state) => state.voteHead);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );

  useEffect(() => {
    if (!students || students.length === 0) dispatch(getStudents());
    if (!voteHeads || voteHeads.length === 0) dispatch(getVoteHeads());
    if (!academicYears || academicYears.length === 0)
      dispatch(getAcademicYears());
  }, [dispatch, students, voteHeads, academicYears]);

  const studentOptions = useMemo(
    () =>
      students?.map((s) => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName} (${s.studentId || "No ID"})`,
      })) || [],
    [students],
  );

  const optionalVoteHeadOptions = useMemo(
    () =>
      voteHeads
        ?.filter((v) => v.isOptional)
        .map((v) => ({
          value: v.id,
          label: `${v.name} (KES ${v.amount?.toLocaleString() || 0})`,
        })) || [],
    [voteHeads],
  );

  const academicYearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const initialValues: StudentFeeAllocationRequestDto = {
    studentId: "",
    voteHeadId: "",
    academicYearId: "",
    narrative: "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(feeAllocationActions.setErrorCreatingFeeAllocation(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={feeAllocationSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: StudentFeeAllocationRequestDto = {
            studentId: rawCleaned.studentId,
            voteHeadId: rawCleaned.voteHeadId,
            academicYearId: rawCleaned.academicYearId,
            narrative: rawCleaned.narrative || null,
          };

          dispatch(createFeeAllocation(cleanedValues));
        }}
      >
        {({ dirty, isValid }: FormikProps<StudentFeeAllocationRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <FormSelectField
                name="studentId"
                label="Select Student"
                options={studentOptions}
              />

              <FormSelectField
                name="voteHeadId"
                label="Select Optional Fee"
                options={optionalVoteHeadOptions}
              />

              <FormSelectField
                name="academicYearId"
                label="Select Academic Year"
                options={academicYearOptions}
              />

              <FormTextField
                name="narrative"
                label="Narrative / Description (Optional)"
                multiline
                rows={2}
              />

              <SubmitButton
                title="Allocate Fee"
                loadingText="Allocating..."
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={<AddCircleOutlineIcon />}
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateFeeAllocationForm;
